import type { Capabilities } from "@/core/capability/detect.ts";
import type { DownloadProgress } from "@/core/storage/moduleCache.ts";
import {
  FeatureMissingError,
  FeatureUnsupportedError,
  type AnyFeaturePack,
  type FeatureId,
  type FeaturePack,
  type PackState,
} from "./types.ts";

export type FeatureRegistry = {
  readonly packs: readonly AnyFeaturePack[];
  state(id: FeatureId): PackState;
  states(): ReadonlyMap<FeatureId, PackState>;
  subscribe(listener: (id: FeatureId, state: PackState) => void): () => void;
  /** Resolves dependencies, deduplicates concurrent calls, and caches the module. */
  ensure<T>(pack: FeaturePack<T>): Promise<T>;
  ensureById(id: FeatureId): Promise<unknown>;
  abort(id: FeatureId): void;
  /** Drops the in-memory module so the next `ensure` reloads it. */
  forget(id: FeatureId): void;
};

export function createFeatureRegistry(
  packs: readonly AnyFeaturePack[],
  capabilities: Capabilities,
): FeatureRegistry {
  const byId = new Map(packs.map((pack) => [pack.id, pack]));
  const states = new Map<FeatureId, PackState>();
  const loading = new Map<FeatureId, Promise<unknown>>();
  const loaded = new Map<FeatureId, unknown>();
  const aborts = new Map<FeatureId, AbortController>();
  const listeners = new Set<(id: FeatureId, state: PackState) => void>();

  for (const pack of packs) {
    const reason = pack.unavailableReason(capabilities);
    states.set(
      pack.id,
      reason === null ? { status: "available" } : { status: "unsupported", reason },
    );
  }

  function setState(id: FeatureId, state: PackState): void {
    states.set(id, state);
    for (const listener of listeners) listener(id, state);
  }

  function requirePack(id: FeatureId): AnyFeaturePack {
    const pack = byId.get(id);
    if (!pack) throw new FeatureMissingError(id);
    return pack;
  }

  async function start(pack: AnyFeaturePack): Promise<unknown> {
    const reason = pack.unavailableReason(capabilities);
    if (reason !== null) throw new FeatureUnsupportedError(pack.id, reason);

    for (const dependency of pack.requires) await load(requirePack(dependency));

    const controller = new AbortController();
    aborts.set(pack.id, controller);
    const report = (progress: DownloadProgress): void =>
      setState(pack.id, { status: "downloading", progress });

    setState(pack.id, {
      status: "downloading",
      progress: { receivedBytes: 0, totalBytes: pack.sizeBytes },
    });

    try {
      const module = await pack.load({ signal: controller.signal, report });
      loaded.set(pack.id, module);
      setState(pack.id, { status: "ready" });
      return module;
    } catch (error) {
      setState(pack.id, { status: "failed", error: messageOf(error) });
      throw error;
    } finally {
      aborts.delete(pack.id);
      loading.delete(pack.id);
    }
  }

  function load(pack: AnyFeaturePack): Promise<unknown> {
    if (loaded.has(pack.id)) return Promise.resolve(loaded.get(pack.id));
    const inFlight = loading.get(pack.id);
    if (inFlight) return inFlight;
    const promise = start(pack);
    loading.set(pack.id, promise);
    return promise;
  }

  return {
    packs,
    state: (id) => states.get(id) ?? { status: "unsupported", reason: "未登録のモジュールです" },
    states: () => new Map(states),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ensure: <T>(pack: FeaturePack<T>) => load(pack) as Promise<T>,
    ensureById: (id) => load(requirePack(id)),
    abort(id) {
      aborts.get(id)?.abort();
    },
    forget(id) {
      loaded.delete(id);
      const pack = byId.get(id);
      if (!pack) return;
      const reason = pack.unavailableReason(capabilities);
      setState(id, reason === null ? { status: "available" } : { status: "unsupported", reason });
    },
  };
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
